import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Zap,
  Search,
  FlaskConical,
  Rocket,
  ArrowRight,
  CheckCircle2,
  Code2,
  Shield,
  Clock,
  MessageSquare,
  CreditCard,
  ShoppingCart,
  Users,
  GitBranch,
  Package,
  Terminal,
  Play,
  Eye,
  Timer,
  Upload,
  BookOpen,
  Share2,
  Boxes,
  Sparkles,
  Building2,
  MousePointerClick
} from 'lucide-react';

const HowItWorksPage = () => {
  const stats = [
    { value: '100+', label: 'Connectors', icon: Boxes },
    { value: '20+', label: 'Industries', icon: Building2 },
    { value: 'Sandbox', label: 'Testing', icon: FlaskConical },
    { value: '1-Click', label: 'Deployment', icon: MousePointerClick },
  ];

  const supportedServices = [
    { name: 'Slack', icon: MessageSquare, color: 'text-pink-400' },
    { name: 'Stripe', icon: CreditCard, color: 'text-purple-400' },
    { name: 'Shopify', icon: ShoppingCart, color: 'text-green-400' },
    { name: 'Salesforce', icon: Users, color: 'text-blue-400' },
    { name: 'GitHub', icon: GitBranch, color: 'text-slate-300' },
  ];

  const exampleActions = [
    { action: 'create_invoice', description: 'Generate invoices in Stripe' },
    { action: 'send_message', description: 'Post messages to Slack channels' },
    { action: 'list_orders', description: 'Fetch orders from Shopify' },
    { action: 'create_ticket', description: 'Open tickets in Jira' },
  ];

  const steps = [
    {
      number: '01',
      title: 'Discover Integrations',
      description: 'Browse the marketplace and find connectors for the software systems you want to integrate with your AI agents.',
      icon: Search,
      color: 'from-blue-500 to-cyan-500',
    },
    {
      number: '02',
      title: 'Test in Sandbox',
      description: 'Safely test connectors in an isolated environment before deploying them to production.',
      icon: FlaskConical,
      color: 'from-amber-500 to-orange-500',
    },
    {
      number: '03',
      title: 'Deploy Instantly',
      description: 'Install connectors using one-click deployment or integrate them via SDK into your AI agent workflow.',
      icon: Rocket,
      color: 'from-emerald-500 to-green-500',
    },
  ];

  const sandboxFeatures = [
    { icon: Play, title: 'Execute Actions', description: 'Run connector actions in real-time' },
    { icon: Terminal, title: 'Simulate Prompts', description: 'Test AI agent prompts and responses' },
    { icon: Eye, title: 'Inspect Responses', description: 'View detailed API response data' },
    { icon: Timer, title: 'Measure Performance', description: 'Track execution time and latency' },
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Save Development Time',
      description: 'Skip weeks of API integration work with pre-built, production-ready connectors.',
    },
    {
      icon: Package,
      title: 'Standardized Connectors',
      description: 'All connectors follow the Pipeyard Connector Standard for consistent AI agent integration.',
    },
    {
      icon: Rocket,
      title: 'Instant Deployment',
      description: 'Deploy integrations in minutes with one-click installation or SDK snippets.',
    },
    {
      icon: Shield,
      title: 'Production Ready',
      description: 'Every connector is verified, tested, and ready for production workloads.',
    },
  ];

  const codeExample = `from pipeyard import connector

# Initialize the Stripe connector
stripe = connector("stripe")

# Create an invoice for a customer
invoice = stripe.create_invoice(
    customer="cus_1234567890",
    amount=5000,  # $50.00
    currency="usd",
    description="Monthly subscription"
)

print(f"Invoice created: {invoice.id}")`;

  return (
    <div className="pt-20 min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="mb-6 bg-primary/10 text-primary border-primary/30 px-4 py-1.5">
              <Zap className="w-3.5 h-3.5 mr-2" />
              Developer Documentation
            </Badge>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6">
              The Integration Marketplace<br />
              <span className="text-gradient">for AI Agents</span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-4 max-w-2xl mx-auto">
              Pipeyard helps developers connect AI agents to real software systems using pre-built integrations.
            </p>
            
            <p className="text-lg text-slate-500 mb-10 max-w-2xl mx-auto">
              Discover connectors, test them safely in a sandbox, and deploy integrations in minutes.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-10">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-[#0B1120] border border-white/5 rounded-xl p-4 text-center hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <stat.icon className="w-5 h-5 text-primary" />
                    <span className="text-2xl font-heading font-bold text-white">{stat.value}</span>
                  </div>
                  <div className="text-xs text-slate-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-white px-8 h-12 text-base"
              asChild
            >
              <Link to="/marketplace" data-testid="explore-connectors-btn">
                Explore Connectors
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* What Pipeyard Does */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
                Platform Overview
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Connect AI Agents to<br />Real Software
              </h2>
              <p className="text-lg text-slate-400 mb-6">
                Pipeyard connectors allow AI agents to interact with external platforms through structured actions. 
                Each connector exposes real API operations that your agents can execute.
              </p>
              <p className="text-slate-500 mb-8">
                Whether you're building autonomous agents, chatbots, or workflow automations, Pipeyard provides 
                the integration layer you need.
              </p>

              {/* Supported Services */}
              <div className="flex flex-wrap gap-3">
                {supportedServices.map((service) => (
                  <div
                    key={service.name}
                    className="flex items-center gap-2 bg-secondary/50 border border-white/5 rounded-lg px-4 py-2"
                  >
                    <service.icon className={`w-4 h-4 ${service.color}`} />
                    <span className="text-sm text-slate-300">{service.name}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-lg px-4 py-2">
                  <span className="text-sm text-primary">+100 more</span>
                </div>
              </div>
            </div>

            {/* Example Actions */}
            <div className="bg-[#0B1120] border border-white/5 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-6">
                <Code2 className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium text-slate-300">Example Connector Actions</span>
              </div>
              <div className="space-y-3">
                {exampleActions.map((item) => (
                  <div
                    key={item.action}
                    className="flex items-center justify-between bg-secondary/30 rounded-lg px-4 py-3 border border-white/5"
                  >
                    <code className="text-sm font-mono text-primary">{item.action}()</code>
                    <span className="text-xs text-slate-500">{item.description}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm text-slate-500">
                All actions are powered by real APIs and follow the Pipeyard Connector Standard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - 3 Steps */}
      <section className="py-20 border-t border-white/5 bg-gradient-to-b from-transparent via-primary/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
              Getting Started
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              How Pipeyard Works
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              From discovery to deployment in three simple steps.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className="relative group"
              >
                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-[60%] w-[80%] h-px bg-gradient-to-r from-white/10 to-transparent" />
                )}
                
                <div className="bg-[#0B1120] border border-white/5 rounded-2xl p-8 h-full hover:border-primary/30 transition-all hover:-translate-y-1">
                  {/* Step Number */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-6`}>
                    <step.icon className="w-7 h-7 text-white" />
                  </div>
                  
                  <div className="text-xs font-mono text-slate-500 mb-2">STEP {step.number}</div>
                  <h3 className="font-heading text-xl font-semibold text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Install a Connector */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-purple-500/10 text-purple-400 border-purple-500/30">
                Integration
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Install a Connector<br />in Minutes
              </h2>
              <p className="text-lg text-slate-400 mb-6">
                Integrate connectors into your AI agents using simple SDK calls. Pipeyard handles the complexity 
                so you can focus on building.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-white font-medium">Authentication</span>
                    <p className="text-sm text-slate-500">Automatic credential management and token refresh</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-white font-medium">API Calls</span>
                    <p className="text-sm text-slate-500">Optimized request handling with retry logic</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-white font-medium">Connector Logic</span>
                    <p className="text-sm text-slate-500">Pre-built action handlers and response parsing</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Code Block */}
            <div className="bg-[#0a0a0f] border border-white/10 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-white/[0.02] border-b border-white/5">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <span className="text-xs text-slate-500 font-mono">example.py</span>
              </div>
              <pre className="p-6 overflow-x-auto">
                <code className="text-sm font-mono">
                  {codeExample.split('\n').map((line, i) => (
                    <div key={i} className="leading-relaxed">
                      {line.includes('from') || line.includes('import') ? (
                        <span><span className="text-purple-400">from</span> <span className="text-cyan-300">pipeyard</span> <span className="text-purple-400">import</span> <span className="text-cyan-300">connector</span></span>
                      ) : line.includes('#') ? (
                        <span className="text-slate-500">{line}</span>
                      ) : line.includes('stripe = ') ? (
                        <span><span className="text-slate-300">stripe</span> <span className="text-slate-500">=</span> <span className="text-cyan-300">connector</span>(<span className="text-amber-300">"stripe"</span>)</span>
                      ) : line.includes('invoice = ') ? (
                        <span><span className="text-slate-300">invoice</span> <span className="text-slate-500">=</span> <span className="text-slate-300">stripe</span>.<span className="text-cyan-300">create_invoice</span>(</span>
                      ) : line.includes('customer=') ? (
                        <span className="text-slate-300">    <span className="text-slate-400">customer</span>=<span className="text-amber-300">"cus_1234567890"</span>,</span>
                      ) : line.includes('amount=') ? (
                        <span className="text-slate-300">    <span className="text-slate-400">amount</span>=<span className="text-emerald-400">5000</span>,  <span className="text-slate-500"># $50.00</span></span>
                      ) : line.includes('currency=') ? (
                        <span className="text-slate-300">    <span className="text-slate-400">currency</span>=<span className="text-amber-300">"usd"</span>,</span>
                      ) : line.includes('description=') ? (
                        <span className="text-slate-300">    <span className="text-slate-400">description</span>=<span className="text-amber-300">"Monthly subscription"</span></span>
                      ) : line.includes(')') && !line.includes('print') ? (
                        <span className="text-slate-300">)</span>
                      ) : line.includes('print') ? (
                        <span><span className="text-cyan-300">print</span>(<span className="text-amber-300">f"Invoice created: </span><span className="text-slate-300">{'{invoice.id}'}</span><span className="text-amber-300">"</span>)</span>
                      ) : line === '' ? (
                        <br />
                      ) : (
                        <span className="text-slate-300">{line}</span>
                      )}
                    </div>
                  ))}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Sandbox Testing */}
      <section className="py-20 border-t border-white/5 bg-gradient-to-b from-transparent via-amber-500/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-amber-500/10 text-amber-400 border-amber-500/30">
              <FlaskConical className="w-3.5 h-3.5 mr-2" />
              Sandbox Environment
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Test Integrations Before<br />Deployment
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Validate your integrations in a safe, isolated environment before using them in production systems.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sandboxFeatures.map((feature) => (
              <div
                key={feature.title}
                className="bg-[#0B1120] border border-white/5 rounded-xl p-6 hover:border-amber-500/30 transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4 group-hover:bg-amber-500/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="font-heading font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Button variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10" asChild>
              <Link to="/sandbox">
                Try the Sandbox
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Build and Publish */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-[#0B1120] border border-white/5 rounded-2xl p-8">
                <div className="space-y-6">
                  {[
                    { step: '1', title: 'Define Actions', description: 'Specify the operations your connector supports' },
                    { step: '2', title: 'Follow PCS Standard', description: 'Use the Pipeyard Connector Standard schema' },
                    { step: '3', title: 'Submit for Review', description: 'Our team reviews for quality and security' },
                    { step: '4', title: 'Publish to Marketplace', description: 'Share with the developer community' },
                  ].map((item, index) => (
                    <div key={item.step} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-mono font-bold text-primary">{item.step}</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-white mb-1">{item.title}</h4>
                        <p className="text-sm text-slate-500">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                <Upload className="w-3.5 h-3.5 mr-2" />
                Contribute
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Build Your Own<br />Integrations
              </h2>
              <p className="text-lg text-slate-400 mb-6">
                Create and publish connectors on the Pipeyard marketplace. Share your integrations with the 
                developer community and help grow the ecosystem.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild>
                  <Link to="/generator">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Use AI Generator
                  </Link>
                </Button>
                <Button variant="outline" className="border-white/10" asChild>
                  <Link to="/submit">
                    <BookOpen className="w-4 h-4 mr-2" />
                    View Documentation
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Developers Use Pipeyard */}
      <section className="py-20 border-t border-white/5 bg-gradient-to-b from-transparent via-primary/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/30">
              Benefits
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Why Developers Choose<br />Pipeyard
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Build faster with production-ready integrations designed for AI agents.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit.title}
                className="bg-[#0B1120] border border-white/5 rounded-xl p-6 hover:border-primary/30 transition-all hover:-translate-y-1 group"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-br from-primary/10 via-purple-500/5 to-pink-500/10 border border-white/10 rounded-3xl p-12 text-center relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
            
            <div className="relative">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                Start Building with Pipeyard
              </h2>
              <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
                Join developers who are building the next generation of AI-powered applications.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-white px-8 h-12"
                  asChild
                >
                  <Link to="/marketplace" data-testid="final-cta-marketplace">
                    Explore the Marketplace
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 px-8 h-12"
                  asChild
                >
                  <Link to="/generator" data-testid="final-cta-generator">
                    Build Your First Connector
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
